import { useEffect, useMemo, useRef, useState } from "react";
import { Map as CoreMap, MapRef, useMap } from "react-map-gl";
import { MapboxOverlay, MapboxOverlayProps } from "@deck.gl/mapbox/typed";
import { useControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box, LinearProgress } from "@mui/material";

import bbox from "@turf/bbox";
import { Feature, FeatureCollection } from "geojson";
import { GeoJsonLayer } from "@deck.gl/layers/typed";
import type { LayersList } from "@deck.gl/core/typed";
import { MapProps } from "../../../../../../Map/types";
import useQueryState from "../../../../../../../hooks/useQueryState";
import useCountries from "../../../../../../../hooks/useCountries";
import { MAPBOX_STYLE, MAPBOX_TOKEN } from "../../../../../../Map/enums";

/* ---------------- DeckGL Overlay ---------------- */
function DeckGLOverlay(props: MapboxOverlayProps & { interleaved?: boolean }) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps({ ...props, getCursor: () => "crosshair" });
  return null;
}

const PopUpMap = ({
  children,
  height,
  width,
  hoveredArea,
  setHoveredArea,
  open,
  setOpen,
}: MapProps) => {
  const mapRef = useRef<MapRef | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const { previewMap } = useMap();
  const {
    queryState: { country, aggregationLevel, year, geographicalUnit },
    setQueryState,
  } = useQueryState();
  const hasFittedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    getCountryAdministrativeUnit: {
      data: geoUnit,
      isFetching: isLoadingCountry,
    },
  } = useCountries({
    country,
    aggregationLevel,
    enabled: true,
  });

  const bounds = useMemo(() => {
    if (!geoUnit?.features?.length) return undefined;

    const featureCollection: FeatureCollection = {
      type: "FeatureCollection",
      features: geoUnit.features as Feature[],
    };

    return bbox(featureCollection);
  }, [geoUnit]);

  useEffect(() => {
    if (!bounds || !mapLoaded || !mapRef.current) return;
    if (hasFittedRef.current) return;

    const map = mapRef.current.getMap();

    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      {
        maxZoom: 8,
        duration: 1000,
      }
    );

    hasFittedRef.current = true;
  }, [bounds, mapLoaded, country, aggregationLevel, geographicalUnit]);

  const geoJsonLayer = useMemo(
    () =>
      country && aggregationLevel && geoUnit
        ? new GeoJsonLayer({
            id: `geojson-layer-${country}-${aggregationLevel}`,
            data: geoUnit?.features ?? [],
            stroked: true,
            filled: true,
            lineWidthMinPixels: 1,
            getFillColor: [224, 64, 251, 51],
            getLineColor: [224, 64, 251, 255],
            autoHighlight: true,
            highlightColor: [255, 255, 0, 180],
            pickable: true,
            onClick: (info: any) => {
              if (!info.object) return;
              const geographicalUnitId = info?.object.properties?.location_id;
              if (geographicalUnitId) {
                setQueryState({ geographicalUnit: geographicalUnitId });
                if (setOpen) setOpen(false);
              }
            },
          })
        : null,
    [geoUnit, country, aggregationLevel]
  );

  const hoveredLayer = useMemo(
    () =>
      country && aggregationLevel && geoUnit && hoveredArea
        ? new GeoJsonLayer({
            id: `geojson-layer-${country}-${aggregationLevel}-${hoveredArea}`,
            data:
              geoUnit?.features?.find(
                (feature) => feature.properties?.location_id === hoveredArea
              ) ?? [],
            pickable: true,
            stroked: true,
            filled: true,
            lineWidthMinPixels: 1,
            getFillColor: [255, 255, 0, 180],
            getLineColor: [180, 180, 180, 255],
            autoHighlight: true,
            highlightColor: [255, 255, 0, 180],
          })
        : null,
    [geoUnit, country, aggregationLevel, hoveredArea]
  );

  const borderLayer = useMemo(
    () =>
      country && aggregationLevel && geoUnit && geographicalUnit
        ? new GeoJsonLayer({
            id: `geojson-layer-border-${country}-${aggregationLevel}-${geographicalUnit}`,
            data:
              geoUnit?.features?.find(
                (item) => item.properties?.location_id === geographicalUnit
              ) ?? [],
            pickable: false,
            stroked: true,
            filled: false,
            getLineColor: [255, 0, 0, 255],
            lineWidthMinPixels: 1,
          })
        : null,
    [geoUnit, country, aggregationLevel, geographicalUnit]
  );

  const allLayers = useMemo<LayersList>(
    () => (geoJsonLayer ? [geoJsonLayer] : []),
    [geoJsonLayer, year, geographicalUnit]
  );

  return (
    <CoreMap
      id="previewMap"
      ref={mapRef}
      mapStyle={MAPBOX_STYLE}
      mapboxAccessToken={MAPBOX_TOKEN}
      cursor="crosshair"
      onMoveStart={() => setIsLoading(true)}
      onMoveEnd={() => setIsLoading(false)}
      onZoomStart={() => setIsLoading(true)}
      onZoomEnd={() => setIsLoading(false)}
      onLoad={() => setMapLoaded(true)}
      style={{ borderRadius: "4px", zIndex: 1, height, width }}
    >
      {children}

      {(isLoading || isLoadingCountry) && (
        <Box sx={{ position: "absolute", zIndex: 10, top: 0, width: "100%" }}>
          <LinearProgress />
        </Box>
      )}

      <DeckGLOverlay
        layers={[...allLayers, hoveredLayer, borderLayer]}
        interleaved
        getTooltip={({ object }) => {
          if (!object) return null;

          const props = (object as any).properties ?? {};
          return { text: props.geounit ?? props.location_id };
        }}
      />
    </CoreMap>
  );
};

export default PopUpMap;
